import http from '../../../api/axios';
import { apiUrl } from '../../../api/apiUrl';
import { handleApiErrors } from '../../../api/apiErrorHandler';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { paths } from '../../RouterContainer';
import { useTranslation } from '../../../context/translationContext';
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs';
import { useEffect, useState } from 'react';
import { INVOICE_TABS } from '../InvoicePage/InvoiceSidePanel';
import { SmartAttachmentForm } from '../../../forms/SmartAttachment/SmartAttachmentForm';
import { SmartAttachmentInputs } from '../../../types/SmartAttachment';
import { InvoiceBase } from '../../../types/Invoice';
import { Loader } from '../../../components/loader/Loader';
import { useLoading } from '../../../context/loadingContext';

/**
 * Page component for creating smart attachments
 */
export const SmartAttachmentCreatePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { documentId } = useParams();
  const { setBreadcrumbs } = useBreadcrumbs();
  const [document, setDocument] = useState<InvoiceBase | null>(null);
  const { startLoading, stopLoading } = useLoading();
  useEffect(() => {
    fetchData();
    setBreadcrumbs([
      { path: `${paths.INVOICES}`, name: 'navigation.documents' },
      { path: `${paths.INVOICE}/${documentId}`, name: 'navigation.document-detail' },
      {
        path: `${paths.INVOICE}/${documentId}/${paths.SMART_ATTACHMENTS}/create`,
        name: 'navigation.smart-attachment-create',
      },
    ]);
  }, []);

  /**
   * Loads document for creating header
   */
  const fetchData = async () => {
    const response = await http.apiGet({
      url: `${apiUrl.ACCOUNTING_DOCUMENT_ITEM}?id=${documentId}`,
    });
    setDocument(response?.data);
  };

  /**
   * Back button action
   */
  const handleOnBack = () =>
    navigate(`/${paths.INVOICE}/${documentId}`, {
      state: { tab: INVOICE_TABS.SMART_ATTACHMENTS },
    });

  /**
   * Handles create action for new smart attachment form submit.
   * Uses company create API.
   * @param values form data
   */
  const handleSubmit = async (values: SmartAttachmentInputs) => {
    try {
      startLoading();
      const response = await http.apiPost({
        url: apiUrl.SMART_ATTACHMENT_CREATE,
        data: { ...values },
      });
      if (response?.status === 201) {
        stopLoading();
        toast.success('Success!');
        navigate(
          `/${paths.INVOICE}/${documentId}/${paths.SMART_ATTACHMENTS}/edit/${response.data?.id}`,
        );
      }
    } catch (err) {
      stopLoading();
      handleApiErrors(err);
    }
  };

  if (!document) return <Loader />;
  return (
    <div>
      <h1 className="text-3xl font-bold">{t('page.smart-attachment.headline-create')}</h1>
      <SmartAttachmentForm onSubmit={handleSubmit} onBack={handleOnBack} document={document} />
    </div>
  );
};
